import { useEffect, useState } from 'react';
import { tokens } from '../../theme';
import { creatorApi, type CreatorClip, apiBase } from '../../lib/api';
import { Button } from '../../components/Button';
import { Badge } from '../../components/Badge';

const API_BASE = apiBase();

function money(c: number) { return `$${(c / 100).toLocaleString()}`; }

export function MyClipsTab() {
  const [clips, setClips] = useState<CreatorClip[] | null>(null);
  const reload = () => creatorApi.clips().then((d) => setClips(d.clips)).catch(() => setClips([]));
  useEffect(() => { reload(); }, []);

  if (!clips) return <p style={{ color: tokens.color.ink[500] }}>Loading your clips...</p>;
  if (clips.length === 0) return <p style={{ color: tokens.color.ink[500] }}>No clips yet. Claim a campaign to get started.</p>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {clips.map((cl) => <ClipRow key={cl.id} clip={cl} onChange={reload} />)}
    </div>
  );
}

function ClipRow({ clip, onChange }: { clip: CreatorClip; onChange: () => void }) {
  const [url, setUrl] = useState('');
  const [needConnect, setNeedConnect] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submittable = clip.status === 'claimed' || clip.status === 'rejected';

  async function submit() {
    setBusy(true); setNeedConnect(null);
    try {
      await creatorApi.submit(clip.id, url);
      onChange();
    } catch (e) {
      const status = (e as { status?: number }).status;
      const detail = (e as { detail?: string }).detail ?? '';
      if (status === 409 && detail.includes('connect_')) {
        setNeedConnect(detail.includes('tiktok') ? 'tiktok' : 'youtube');
      }
    } finally { setBusy(false); }
  }

  return (
    <div style={{ background: tokens.color.paper['00'], border: `1px solid ${tokens.color.line[100]}`, borderRadius: 12, padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <span style={{ fontFamily: tokens.font.mono, fontSize: 12 }}>{clip.serial_code} · {clip.campaign_title}</span>
        <Badge status={clip.status as Parameters<typeof Badge>[0]['status']}>{clip.status}</Badge>
      </div>
      {clip.status !== 'claimed' && clip.status !== 'rejected' && (
        <div style={{ marginTop: 8, fontFamily: tokens.font.mono, fontSize: 12, color: tokens.color.ink[700] }}>
          {clip.platform ?? '-'} · {clip.verified_views.toLocaleString()} views · {money(clip.earnings_cents)}
        </div>
      )}
      {clip.status === 'rejected' && clip.rejection_reason && (
        <p style={{ marginTop: 8, fontSize: 12, color: tokens.color.accent.rust }}>Reason: {clip.rejection_reason}</p>
      )}
      {submittable && (
        <div style={{ marginTop: 12 }}>
          {needConnect ? (
            <a href={`${API_BASE}/auth/oauth/${needConnect}`}><Button size="sm">Connect {needConnect === 'tiktok' ? 'TikTok' : 'YouTube'} to submit</Button></a>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Paste your clip URL" style={{ flex: 1 }} />
              <Button size="sm" loading={busy} disabled={!url} onClick={submit}>Submit</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
